import type { OrganizationDetail } from '@/api/organizations'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Users, Stack, GearSix, ArrowRight } from '@phosphor-icons/react'

interface OrgOverviewTabProps {
  org: OrganizationDetail
  onNavigateToUsers?: () => void
}

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays < 1) return 'today'
  if (diffDays === 1) return '1 day ago'
  if (diffDays < 7) return `${diffDays} days ago`
  const diffWeeks = Math.floor(diffDays / 7)
  if (diffWeeks === 1) return '1 week ago'
  if (diffWeeks < 5) return `${diffWeeks} weeks ago`
  const diffMonths = Math.floor(diffDays / 30)
  if (diffMonths === 1) return '1 month ago'
  if (diffMonths < 12) return `${diffMonths} months ago`
  const diffYears = Math.floor(diffDays / 365)
  if (diffYears === 1) return '1 year ago'
  return `${diffYears} years ago`
}

export function OrgOverviewTab({ org, onNavigateToUsers }: OrgOverviewTabProps) {
  const inactiveCount = org.user_count - org.active_user_count
  const usagePercent = org.max_users > 0
    ? Math.round((org.user_count / org.max_users) * 100)
    : 0
  const barColor = usagePercent > 95
    ? 'bg-red-500'
    : usagePercent > 80
      ? 'bg-amber-500'
      : 'bg-primary'

  const hasSettings = org.settings && Object.keys(org.settings).length > 0

  return (
    <div className="space-y-6">
      {/* Header summary row */}
      <div className="flex items-center gap-3 flex-wrap text-sm">
        <Badge variant={org.is_active ? 'default' : 'destructive'} className="gap-1.5">
          <span
            className={`inline-block h-1.5 w-1.5 rounded-full ${
              org.is_active ? 'bg-emerald-300' : 'bg-red-300'
            }`}
          />
          {org.is_active ? 'Active' : 'Inactive'}
        </Badge>

        {org.primary_vertical_slug && (
          <>
            <span className="text-muted-foreground/40">|</span>
            <Badge variant="secondary">{org.primary_vertical_slug}</Badge>
          </>
        )}

        <span className="text-muted-foreground/40">|</span>
        <Badge variant="outline" className="capitalize">
          {org.subscription_tier}
        </Badge>

        <span className="text-muted-foreground/40">|</span>
        <span className="font-mono text-muted-foreground">{org.slug}</span>

        <span className="text-muted-foreground/40">|</span>
        <span className="text-muted-foreground">
          Created {timeAgo(org.created_at)}
        </span>
      </div>

      {/* Team (capacity + users) */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5 text-muted-foreground" />
            Team
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Capacity bar */}
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">Capacity</span>
              <span className="text-sm font-medium">
                {org.user_count} / {org.max_users} seats
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted">
              <div
                className={`h-2 rounded-full transition-all ${barColor}`}
                style={{ width: `${Math.min(usagePercent, 100)}%` }}
              />
            </div>
          </div>

          {/* Stat row */}
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg border p-4 text-center">
              <p className="text-3xl font-semibold">{org.active_user_count}</p>
              <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
                Active
              </p>
            </div>
            <div className="rounded-lg border p-4 text-center">
              <p className="text-3xl font-semibold">{inactiveCount}</p>
              <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
                Inactive
              </p>
            </div>
            <div className="rounded-lg border p-4 text-center">
              <p className="text-3xl font-semibold">{org.admin_count}</p>
              <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
                Admins
              </p>
            </div>
          </div>

          {onNavigateToUsers && (
            <div className="border-t pt-4">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground"
                onClick={onNavigateToUsers}
              >
                Manage Users
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Verticals */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Stack className="h-5 w-5 text-muted-foreground" />
            Verticals
          </CardTitle>
        </CardHeader>
        <CardContent>
          {org.active_verticals.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {org.active_verticals.map((vertical) => (
                <Badge key={vertical} variant="secondary" className="gap-1.5 py-1 px-3">
                  <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                  {vertical}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No verticals configured
            </p>
          )}
        </CardContent>
      </Card>

      {/* Configuration */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <GearSix className="h-5 w-5 text-muted-foreground" />
            Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasSettings ? (
            <pre className="rounded-lg bg-muted/50 p-4 text-xs font-mono overflow-x-auto">
              {JSON.stringify(org.settings, null, 2)}
            </pre>
          ) : (
            <p className="text-sm text-muted-foreground">
              No custom settings
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
